import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveHeader,
  ResponsiveContent,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';

import { Check } from 'lucide-react';

const Frame800: React.FC = () => {
  const navigate = useNavigate();

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <ResponsiveHeader title="Organize Your Personal Accounts and Belongings" />

        <div className="max-w-4xl space-y-8">
          {/* Introduction */}
          <div className="space-y-4">
            <p className="text-stone-800 text-lg font-medium font-['Work_Sans'] leading-relaxed">
              Your personal accounts and belongings reflect the life you’ve lived from important
              documents and digital accounts to meaningful items you’ve collected along the way.
              Taking time to organize these details now helps ensure nothing important is overlooked
              and gives your loved ones clarity and guidance when they need it most.
            </p>
          </div>

          {/* What You'll Do */}
          <div className="bg-white rounded-lg border border-neutral-200 p-6">
            <h2 className="text-xl font-semibold text-cyan-900 mb-4">What You’ll Do</h2>

            <div className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white stroke-[3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-1">
                    Capture important documents, digital accounts, and personal items
                  </h3>
                  <p className="text-stone-600">
                    Create a clear record of the things that matter, from practical account details
                    to sentimental belongings and important paperwork.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white stroke-[3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-1">
                    Group belongings by category so they’re easy to find
                  </h3>
                  <p className="text-stone-600">
                    Organize items in a way that makes them simpler to review, update, and locate
                    later when your loved ones may need guidance.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white stroke-[3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-1">
                    Add notes or instructions for items that need special handling
                  </h3>
                  <p className="text-stone-600">
                    Include anything your loved ones should know, especially for meaningful,
                    valuable, or sensitive items that need extra care.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-cyan-700 flex items-center justify-center">
                  <Check className="w-4 h-4 text-white stroke-[3]" />
                </div>
                <div>
                  <h3 className="font-semibold text-stone-800 mb-1">
                    Keep everything organized in one secure place
                  </h3>
                  <p className="text-stone-600">
                    Bring important details together so they’re easier to manage now and easier for
                    others to understand later.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Why This Matters */}
          <div className="bg-cyan-50 border border-cyan-100 rounded-lg p-6">
            <h2 className="text-lg font-semibold text-cyan-900 mb-3">Why This Matters</h2>
            <ul className="space-y-2 text-stone-700">
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Helps loved ones understand what exists and where to find it</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Reduces stress and guesswork during an already emotional time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Ensures meaningful or valuable items are handled according to your wishes</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-700 mt-1">✓</span>
                <span>Creates a clear, organized record that can be updated anytime</span>
              </li>
            </ul>
          </div>

          {/* Time Estimate */}
          <div className="text-center text-stone-600">
            <p className="text-sm">
              <span className="font-semibold">Estimated time:</span> Approximately 30–60 minutes,
              depending on how much information you’d like to add now.
            </p>
            <p className="text-sm mt-2">You can always return and update this later.</p>
          </div>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Organize Personal Accounts and Belongings"
        subtitle="Document your personal accounts and meaningful items"
        progressPercent={0}
        onBack={() => navigate('/frame-140')}
        onNext={() => navigate('/frame-801')}
        nextText="Get Started"
        backText="Back to Dashboard"
      />
    </ResponsiveContainer>
  );
};

export default Frame800;